import { SCREEN_LEVEL_LABELS, SCREEN_LEVELS, screenLevelFor, type ScreenLevel } from "@shared/screenLevel";
import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Copy, KeyRound, Plus, Share2, Trash2, UserPlus } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  IconButton,
  NumberStepper,
  SectionTitle,
  Segmented,
  Select,
  TextField,
  Toggle,
  cx,
} from "@/components/ui";
import { Avatar, kidColor } from "@/components/bits";
import { EmojiAvatarPicker } from "@/components/EmojiPicker";
import { useToast } from "@/components/Toast";
import { api } from "@/lib/api";
import type { PasskeyDTO } from "@shared/types";
import { useAction, useRewardTypes } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { AVATARS, COLORS } from "@shared/templates";
import { isValidPin, PIN_RULE } from "@shared/pin";
import type { FamilyDTO, MemberDTO } from "@shared/types";

// ---------- local helpers ----------

function useCopy() {
  const toast = useToast();
  return async (text: string, what = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.show(what, "success");
    } catch {
      toast.show("Couldn't copy — press and hold to copy it instead.", "error");
    }
  };
}

const thisYear = () => new Date().getFullYear();

type NegativeMode = "default" | "allow" | "never";

interface KidDraft {
  id?: string;
  name: string;
  avatar: string;
  color: string;
  birthYear: string;
  interests: string;
  favoriteRewardTypeId: string;
  screenLevel: ScreenLevel | "";
  pin: string;
  /** Edit only: show the "Reset PIN" field */
  resetPin: boolean;
  negative: NegativeMode;
  negativeFloorOverride: number | null;
}

/** A kid's passkeys, added from their own Me tab: parents can see and remove them. */
function KidPasskeys({ kid }: { kid: MemberDTO }) {
  const qc = useQueryClient();
  const key = ["kid-passkeys", kid.id];
  const list = useQuery<{ passkeys: PasskeyDTO[] }>({ queryKey: key, queryFn: () => api.get(`/family/kids/${kid.id}/passkeys`) });
  const remove = useAction((id: string) => api.del(`/family/kids/${kid.id}/passkeys/${encodeURIComponent(id)}`), {
    success: "Passkey removed",
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
  const passkeys = list.data?.passkeys ?? [];
  return (
    <div className="mt-5">
      <div className="text-sm font-bold">Passkeys</div>
      {passkeys.length === 0 ? (
        <p className="mt-1 text-sm text-muted">None yet. {kid.name} can add one from the Me tab, to sign in without a PIN on paired devices.</p>
      ) : (
        <ul className="mt-1 divide-y divide-line">
          {passkeys.map((p) => (
            <li key={p.id} className="flex items-center gap-2 py-2">
              <span className="min-w-0 flex-1 truncate text-sm font-semibold">{p.label}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.confirm(`Remove ${kid.name}'s passkey on ${p.label}?`) && remove.mutate(p.id)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function newKidDraft(taken: MemberDTO[]): KidDraft {
  const usedColors = new Set(taken.map((k) => k.color));
  const usedAvatars = new Set(taken.map((k) => k.avatar));
  return {
    name: "",
    avatar: AVATARS.find((a) => !usedAvatars.has(a)) ?? AVATARS[0],
    color: COLORS.find((c) => !usedColors.has(c)) ?? COLORS[0],
    birthYear: "",
    interests: "",
    favoriteRewardTypeId: "",
    screenLevel: "",
    pin: "",
    resetPin: false,
    negative: "default",
    negativeFloorOverride: null,
  };
}

function draftFromKid(k: MemberDTO): KidDraft {
  return {
    id: k.id,
    name: k.name,
    avatar: k.avatar,
    color: k.color,
    birthYear: k.birthYear ? String(k.birthYear) : "",
    interests: k.interests ?? "",
    favoriteRewardTypeId: k.favoriteRewardTypeId ?? "",
    screenLevel: k.screenLevel ?? "",
    pin: "",
    resetPin: false,
    negative: k.allowNegativeOverride === null ? "default" : k.allowNegativeOverride ? "allow" : "never",
    negativeFloorOverride: k.negativeFloorOverride,
  };
}

/** Normalized API fields for a draft. */
function draftFields(d: KidDraft) {
  const year = d.birthYear.trim() ? Number(d.birthYear) : null;
  return {
    name: d.name.trim(),
    avatar: d.avatar,
    color: d.color,
    birthYear: year,
    interests: d.interests.trim() || null,
    favoriteRewardTypeId: d.favoriteRewardTypeId || null,
    screenLevel: d.screenLevel || null,
    allowNegativeOverride: d.negative === "default" ? null : d.negative === "allow",
    negativeFloorOverride: d.negative === "allow" ? d.negativeFloorOverride : null,
  };
}

function validate(d: KidDraft): string | null {
  if (!d.name.trim()) return "Add a name";
  if (d.birthYear.trim()) {
    const y = Number(d.birthYear);
    if (!Number.isInteger(y) || y < thisYear() - 25 || y > thisYear()) return "Check the birth year";
  }
  const needsPin = !d.id || d.resetPin;
  if (needsPin && !isValidPin(d.pin)) return d.id && !d.pin ? "Enter a new PIN" : PIN_RULE;
  return null;
}

// ---------- page ----------

export function FamilySettings() {
  const { kids, parents, user, family } = useSession();
  const { data: rewards } = useRewardTypes();
  const toast = useToast();
  const rewardById = (id: string | null) => rewards?.rewardTypes.find((r) => r.id === id);
  const [draft, setDraft] = useState<KidDraft | null>(null);
  const [invite, setInvite] = useState<{ code: string; expiresAt: number; url: string } | null>(null);
  // Stable close handlers: the Sheet re-runs its open effect (and refocuses) when onClose changes.
  const closeDraft = useCallback(() => setDraft(null), []);
  const closeInvite = useCallback(() => setInvite(null), []);

  const saveKid = useAction(
    (d: KidDraft) => {
      const fields = draftFields(d);
      if (!d.id) return api.post<MemberDTO>("/family/kids", { ...fields, pin: d.pin });
      const original = kids.find((k) => k.id === d.id);
      const before = original ? draftFields(draftFromKid(original)) : null;
      const body: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(fields)) {
        if (!before || before[key as keyof typeof before] !== value) body[key] = value;
      }
      if (d.resetPin && d.pin) body.pin = d.pin;
      return api.patch<MemberDTO>(`/family/kids/${d.id}`, body);
    },
    {
      onSuccess: (kid, d) => {
        toast.show(d.id ? `${kid.name} saved` : `${kid.name} added 🎉`, "success");
        setDraft(null);
      },
    },
  );

  const removeKid = useAction((k: MemberDTO) => api.del(`/family/kids/${k.id}`), {
    success: "Deleted",
    onSuccess: () => setDraft(null),
  });

  const removeParent = useAction((p: MemberDTO) => api.del(`/family/parents/${p.id}`), { success: "Removed" });

  const createInvite = useAction(() => api.post<{ code: string; expiresAt: number; url: string }>("/family/invites"), {
    onSuccess: (res) => setInvite(res),
  });

  const editing = draft?.id ? kids.find((k) => k.id === draft.id) : undefined;
  const error = draft ? validate(draft) : null;

  return (
    <div>
      <PageHeader
        title="Family & kids"
        subtitle={family.name}
        back="/parent/more"
        actions={
          kids.length > 0 ? (
            <Button size="sm" onClick={() => setDraft(newKidDraft(kids))}>
              <Plus className="size-4" /> Add kid
            </Button>
          ) : undefined
        }
      />

      <SectionTitle>Kids</SectionTitle>
      {kids.length === 0 ? (
        <EmptyState
          icon="🧒"
          title="No kids yet"
          action={<Button onClick={() => setDraft(newKidDraft(kids))}>Add your first kid</Button>}
        >
          Each kid gets their own PIN to sign in on devices you pair (Settings → Kid devices).
        </EmptyState>
      ) : (
        <div className="space-y-2">
          {kids.map((k) => {
            const fav = rewardById(k.favoriteRewardTypeId);
            const details = [k.age !== null ? `Age ${k.age}` : null, k.interests].filter(Boolean).join(" · ");
            return (
              <Card
                key={k.id}
                className="cursor-pointer p-3 transition hover:ring-brand/40"
                onClick={() => setDraft(draftFromKid(k))}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setDraft(draftFromKid(k))}
              >
                <div className="flex items-center gap-3">
                  <Avatar member={k} size="lg" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-lg font-extrabold">{k.name}</div>
                    <div className="truncate text-sm text-muted">{details || "Tap to add age and interests"}</div>
                    {!k.hasPin && (
                      <div className="mt-1">
                        <Badge tone="danger">No PIN yet: tap to set one</Badge>
                      </div>
                    )}
                    {fav && (
                      <div className="mt-1">
                        <Badge tone="coin">
                          {fav.icon} Sees earnings as {fav.name}
                        </Badge>
                      </div>
                    )}
                  </div>
                  <ChevronRight className="size-5 shrink-0 text-faint" />
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <SectionTitle>Parents</SectionTitle>
      <Card className="p-1.5">
        <ul className="divide-y divide-line/70">
          {parents.map((p) => {
            const isMe = p.id === user.id;
            return (
              <li key={p.id} className="flex items-center gap-3 px-3 py-3">
                <Avatar member={p} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-extrabold">{p.name}</span>
                    {isMe && <Badge tone="brand">You</Badge>}
                  </div>
                  {p.email && <div className="truncate text-sm text-muted">{p.email}</div>}
                </div>
                {!isMe && (
                  <IconButton
                    label={`Remove ${p.name}`}
                    disabled={removeParent.isPending}
                    onClick={() => {
                      if (window.confirm(`Remove ${p.name} from ${family.name}? Their parent account is deleted and they're signed out everywhere.`)) {
                        removeParent.mutate(p);
                      }
                    }}
                  >
                    <Trash2 className="size-5 text-danger" />
                  </IconButton>
                )}
              </li>
            );
          })}
        </ul>
      </Card>
      <Button variant="secondary" block className="mt-3" onClick={() => createInvite.mutate()} loading={createInvite.isPending}>
        <UserPlus className="size-4" /> Invite a co-parent
      </Button>

      {/* Kid create / edit */}
      <Sheet
        open={!!draft}
        onClose={closeDraft}
        title={draft?.id ? `Edit ${editing?.name ?? "kid"}` : "Add a kid"}
        footer={
          <div className="space-y-2">
            {error && draft && (draft.name || draft.pin || draft.id) ? (
              <p className="text-center text-sm font-semibold text-danger">{error}</p>
            ) : null}
            <div className="flex gap-2">
              {editing && (
                <Button
                  variant="secondary"
                  aria-label={`Delete ${editing.name}`}
                  loading={removeKid.isPending}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Permanently delete ${editing.name} and all of their information? Their balance, history, goals, notes and requests are erased and can't be recovered. Export your family's data first if you want a copy.`,
                      )
                    ) {
                      removeKid.mutate(editing);
                    }
                  }}
                >
                  <Trash2 className="size-4 text-danger" />
                </Button>
              )}
              <Button block onClick={() => draft && saveKid.mutate(draft)} loading={saveKid.isPending} disabled={!!error}>
                {draft?.id ? "Save" : "Add kid"}
              </Button>
            </div>
          </div>
        }
      >
        {draft && (
          <KidForm
            draft={draft}
            setDraft={setDraft}
            family={family}
            rewards={(rewards?.rewardTypes ?? []).filter((r) => r.active || r.id === draft.favoriteRewardTypeId)}
          />
        )}
        {draft && editing && <KidPasskeys kid={editing} />}
      </Sheet>

      {/* Invite */}
      <Sheet open={!!invite} onClose={closeInvite} title="Invite a co-parent">
        {invite && <InviteDetails invite={invite} familyName={family.name} />}
      </Sheet>
    </div>
  );
}

// ---------- kid form ----------

function KidForm({
  draft,
  setDraft,
  family,
  rewards,
}: {
  draft: KidDraft;
  setDraft: (d: KidDraft) => void;
  family: FamilyDTO;
  rewards: { id: string; name: string; icon: string }[];
}) {
  const set = <K extends keyof KidDraft>(key: K, value: KidDraft[K]) => setDraft({ ...draft, [key]: value });
  const year = Number(draft.birthYear);
  const age = draft.birthYear.length === 4 && Number.isInteger(year) && year <= thisYear() ? thisYear() - year : null;
  const displayName = draft.name.trim() || "your kid";
  const familyDefault = family.allowNegative
    ? `can go down to −${Math.abs(family.negativeFloor).toLocaleString()} ${family.currencyName}`
    : "can't go below zero";

  return (
    <div className="space-y-5">
      <div className="flex justify-center pt-1">
        <Avatar member={{ avatar: draft.avatar, color: draft.color, name: draft.name || "New kid" }} size="xl" />
      </div>

      <TextField label="Name" placeholder="Maya" value={draft.name} maxLength={40} onChange={(e) => set("name", e.target.value)} />

      <Field label="Avatar">
        <EmojiAvatarPicker value={draft.avatar} onChange={(avatar) => set("avatar", avatar)} />
      </Field>

      <Field label="Color">
        <div className="flex flex-wrap gap-2.5">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Color ${c}`}
              aria-pressed={draft.color === c}
              onClick={() => set("color", c)}
              className={cx(
                "size-10 rounded-full transition",
                draft.color === c ? "scale-110 ring-[3px] ring-ink ring-offset-2 ring-offset-card" : "hover:scale-105",
              )}
              style={{ background: kidColor(c).bg }}
            />
          ))}
        </div>
      </Field>

      <TextField
        label="Birth year (optional)"
        type="number"
        inputMode="numeric"
        placeholder={String(thisYear() - 9)}
        min={thisYear() - 25}
        max={thisYear()}
        value={draft.birthYear}
        onChange={(e) => set("birthYear", e.target.value.replace(/\D/g, "").slice(0, 4))}
        hint={age !== null ? `Turns ${age} this year — used for age-appropriate suggestions.` : "Used for age-appropriate suggestions."}
      />

      <Field label="Screens" hint={`What ${displayName} sees when they sign in. Automatic changes as they grow.`}>
        <Select value={draft.screenLevel} onChange={(e) => set("screenLevel", e.target.value as KidDraft["screenLevel"])}>
          <option value="">Automatic{age !== null ? ` (${SCREEN_LEVEL_LABELS[screenLevelFor(age)].split(":")[0]})` : ""}</option>
          {SCREEN_LEVELS.map((l) => (
            <option key={l} value={l}>
              {SCREEN_LEVEL_LABELS[l]}
            </option>
          ))}
        </Select>
      </Field>

      <TextField
        label="Interests (optional)"
        placeholder="Minecraft, soccer, art"
        value={draft.interests}
        maxLength={300}
        onChange={(e) => set("interests", e.target.value)}
        hint="Used to suggest meaningful rewards — e.g. Minecraft, soccer, art"
      />

      <Field
        label="Show my earnings as…"
        hint={`How ${displayName} sees their balance in meaningful terms — like “≈ 45 min of screen time”.`}
      >
        <Select value={draft.favoriteRewardTypeId} onChange={(e) => set("favoriteRewardTypeId", e.target.value)}>
          <option value="">Just {family.currencyName}</option>
          {rewards.map((r) => (
            <option key={r.id} value={r.id}>
              {r.icon} {r.name}
            </option>
          ))}
        </Select>
      </Field>

      {draft.id ? (
        draft.resetPin ? (
          <div className="space-y-1.5">
            <PinField draft={draft} setDraft={setDraft} label="New PIN" hint={`Changing the PIN signs ${displayName} out on every device.`} />
            <button type="button" className="text-sm font-bold text-brand" onClick={() => setDraft({ ...draft, resetPin: false, pin: "" })}>
              Keep the current PIN
            </button>
          </div>
        ) : (
          <Button variant="secondary" block onClick={() => setDraft({ ...draft, resetPin: true, pin: "" })}>
            <KeyRound className="size-4" /> Reset PIN
          </Button>
        )
      ) : (
        <PinField draft={draft} setDraft={setDraft} label="PIN" hint={`${displayName === "your kid" ? "Your kid" : displayName} types this to sign in. 4–6 digits.`} />
      )}

      <Field label="Spending below zero">
        <Segmented
          value={draft.negative}
          onChange={(negative) => setDraft({ ...draft, negative, negativeFloorOverride: negative === "allow" ? draft.negativeFloorOverride : null })}
          options={[
            { value: "default", label: "Family default" },
            { value: "allow", label: "Allow" },
            { value: "never", label: "Never" },
          ]}
        />
        <p className="text-sm text-muted">
          {draft.negative === "default"
            ? `Uses the family rule: ${displayName} ${familyDefault}.`
            : draft.negative === "allow"
              ? `${displayName[0].toUpperCase()}${displayName.slice(1)} can go into the negative after a deduction, then work it off.`
              : `${displayName[0].toUpperCase()}${displayName.slice(1)}'s balance never goes below zero.`}
        </p>
      </Field>

      {draft.negative === "allow" && (
        <div className="space-y-3 rounded-3xl bg-sunken/60 p-4 ring-1 ring-line/60">
          <Toggle
            checked={draft.negativeFloorOverride !== null}
            onChange={(on) => set("negativeFloorOverride", on ? -Math.abs(family.negativeFloor || 50) : null)}
            label="Custom limit"
            description={
              draft.negativeFloorOverride === null
                ? `Off — uses the family limit (−${Math.abs(family.negativeFloor).toLocaleString()}).`
                : `${displayName[0].toUpperCase()}${displayName.slice(1)} can go down to −${Math.abs(draft.negativeFloorOverride).toLocaleString()} ${family.currencyName}.`
            }
          />
          {draft.negativeFloorOverride !== null && (
            <NumberStepper
              value={Math.abs(draft.negativeFloorOverride)}
              onChange={(v) => set("negativeFloorOverride", -Math.round(v))}
              min={0}
              max={10_000}
              step={10}
              suffix={family.currencyIcon}
            />
          )}
        </div>
      )}
    </div>
  );
}

function PinField({
  draft,
  setDraft,
  label,
  hint,
}: {
  draft: KidDraft;
  setDraft: (d: KidDraft) => void;
  label: string;
  hint: string;
}) {
  return (
    <TextField
      label={label}
      inputMode="numeric"
      autoComplete="off"
      pattern="\d*"
      placeholder="••••"
      maxLength={6}
      className="tracking-[0.4em]"
      value={draft.pin}
      onChange={(e) => setDraft({ ...draft, pin: e.target.value.replace(/\D/g, "").slice(0, 6) })}
      hint={hint}
    />
  );
}

// ---------- invite ----------

function InviteDetails({ invite, familyName }: { invite: { code: string; expiresAt: number; url: string }; familyName: string }) {
  const copy = useCopy();
  const toast = useToast();
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const expires = new Date(invite.expiresAt).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });

  async function share() {
    try {
      await navigator.share({
        title: `Join ${familyName} on Meaningful Rewards`,
        text: `Join ${familyName} on Meaningful Rewards — use code ${invite.code}`,
        url: invite.url,
      });
    } catch (err) {
      if ((err as Error).name !== "AbortError") toast.show("Couldn't open the share menu — copy the link instead.", "error");
    }
  }

  return (
    <div className="space-y-4 pb-2">
      <p className="text-sm text-muted">
        Send this to your partner or another caregiver. They'll create their own sign-in and join <b className="text-ink">{familyName}</b>{" "}
        as a parent.
      </p>
      <div className="rounded-3xl bg-brand-soft px-4 py-5 text-center">
        <div className="text-xs font-extrabold uppercase tracking-wide text-brand-strong">Invite code</div>
        <div className="mt-1 select-all font-mono text-4xl font-black tracking-[0.2em] text-brand-strong">{invite.code}</div>
        <div className="mt-1 text-xs font-semibold text-brand-strong/80">Expires {expires} · works once</div>
      </div>
      <div className="flex items-center gap-2 rounded-2xl bg-sunken px-3 py-2 ring-1 ring-line">
        <span className="min-w-0 flex-1 truncate select-all font-mono text-sm">{invite.url}</span>
      </div>
      <div className="flex gap-2">
        <Button variant="secondary" block onClick={() => copy(invite.url, "Invite link copied")}>
          <Copy className="size-4" /> Copy link
        </Button>
        {canShare && (
          <Button block onClick={share}>
            <Share2 className="size-4" /> Share
          </Button>
        )}
      </div>
    </div>
  );
}
